import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { PROGRAMS } from "../ProgramsPage";

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    courseId: "",
    courseTitle: "",
    description: "",
    duration: "120 Hours",
    mode: "Online",
    mentorName: "",
    totalModules: 6,
    courseFee: 315,
    status: "active",
  });

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "courses"));
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (list.length === 0) {
        // Seed default programs as courses
        for (const p of PROGRAMS) {
          await setDoc(doc(db, "courses", p.id), {
            courseId: p.id,
            courseTitle: p.name,
            description: p.desc,
            duration: p.duration,
            mode: "Online",
            mentorName: "Faculty Mentor",
            totalModules: 6,
            courseFee: p.price,
            status: "active",
            createdAt: serverTimestamp(),
          });
        }
        const snap2 = await getDocs(collection(db, "courses"));
        list = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
      setCourses(list);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ courseId: "", courseTitle: "", description: "", duration: "120 Hours", mode: "Online", mentorName: "", totalModules: 6, courseFee: 315, status: "active" });
    setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...c });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.courseTitle) { toast.error("Title required"); return; }
    const id = editing || form.courseId || `course-${Date.now()}`;
    try {
      await setDoc(doc(db, "courses", id), { ...form, courseId: id, updatedAt: serverTimestamp() }, { merge: true });
      toast.success(editing ? "Course updated" : "Course added");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    try {
      await deleteDoc(doc(db, "courses", id));
      toast.success("Course deleted");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-courses">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Course Management</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Courses</h1>
          <p className="mt-1 text-slate-600">{courses.length} courses</p>
        </div>
        <button onClick={openAdd} data-testid="add-course-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> Add Course
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-heading font-semibold text-slate-900">{c.courseTitle}</h3>
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold capitalize">{c.status}</span>
              </div>
              <p className="text-sm text-slate-600 mb-3 line-clamp-2">{c.description}</p>
              <div className="text-xs text-slate-500 space-y-1">
                <div>Duration: <span className="font-semibold text-slate-700">{c.duration}</span></div>
                <div>Mode: <span className="font-semibold text-slate-700">{c.mode}</span></div>
                <div>Fee: <span className="font-semibold text-blue-600">₹{c.courseFee}</span></div>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => openEdit(c)} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-xs font-semibold">
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => remove(c.id)} className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Course" : "Add Course"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <Inp label="Course Title" value={form.courseTitle} onChange={(v) => setForm({ ...form, courseTitle: v })} />
            <Inp label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea />
            <div className="grid grid-cols-2 gap-3">
              <Inp label="Duration" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} />
              <Inp label="Mode" value={form.mode} onChange={(v) => setForm({ ...form, mode: v })} />
              <Inp label="Mentor" value={form.mentorName} onChange={(v) => setForm({ ...form, mentorName: v })} />
              <Inp label="Fee (₹)" type="number" value={form.courseFee} onChange={(v) => setForm({ ...form, courseFee: Number(v) })} />
            </div>
            <button onClick={save} data-testid="save-course-btn" className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              {editing ? "Update Course" : "Add Course"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Inp = ({ label, value, onChange, type = "text", textarea }) => (
  <div>
    <label className="block text-xs text-slate-600 mb-1 font-medium">{label}</label>
    {textarea ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
    ) : (
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
    )}
  </div>
);

export default AdminCourses;
